/**
 * `GET /reports/ar-aging` — the payload, typed to what the handler actually
 * returns (api#796, `src/lib/ar-by-customer.ts`).
 *
 * Typed by hand rather than through `api-manifest.d.ts` for the reason written
 * at the head of `payroll-summary-report.ts`: this repo's manifest mirror
 * predates both endpoints, and re-mirroring carries an unrelated route removal
 * with it.
 *
 * ── `null` IS THE POINT OF THIS SHAPE, NOT AN EDGE CASE ──────────────────
 *
 * Every money key here is `number | null`, and the `null` is load-bearing.
 * When EVERY revenue ticket of a customer carries no price, the server sends
 * `null` rather than a summed 0.00 — because a SUM over unpriced tickets is
 * 0.00, and 0.00 in a revenue column is a claim that the work was worth
 * nothing. Dutch's Coelacanth Energy is the live case: 6 of 6 revenue tickets
 * unpriced, so it reads "—".
 *
 * `unpriced_ticket_count` is what makes the dash legible. A dash on its own
 * says "we do not know"; a dash beside "6 unpriced" says why. Render them
 * together or the screen has told half the story.
 *
 * A customer with SOME priced tickets gets the sum of those, with the unpriced
 * count beside it — a real figure that is known to be short, which is a third
 * state again and must not be flattened into either of the other two.
 *
 * ── RECONCILIATION, WHICH THE SCREEN MUST NOT RE-DERIVE ──────────────────
 *
 *   per row:  paid + billed_unpaid + unbilled = revenue
 *             Σ aging buckets                 = outstanding
 *   totals:   every figure = Σ of the rows, in cents
 *
 * These hold by construction on the server and the API's own test asserts
 * them. CLAUDE.md's fifth non-negotiable applies with force here: this screen
 * recomputes none of them. The previous version of this page recomputed all of
 * them, from a payload carrying none of the inputs, and that is what fe#725
 * measured and fe#727 withdrew the catalog row for.
 */

/** The four buckets, keyed on `tickets.approved_at` in the tenant's zone. */
export type ArAgingBucketKey = '0_30' | '31_60' | '61_90' | '90_plus';

export type ArAging = Record<ArAgingBucketKey, number | null>;

export interface ArAgingBucket {
  key: ArAgingBucketKey;
  /** `null` on the first bucket — it has no lower edge. */
  min_days: number | null;
  /** `null` on the last bucket — it has no upper edge. */
  max_days: number | null;
}

export interface ArAgingCustomerRow {
  /**
   * `null` for tickets with no customer on them. Its own row, never merged
   * into another customer by name — a name is not a key, and the row exists so
   * that a ticket nobody can bill never disappears from the total.
   */
  customer_id: string | null;
  customer_name: string | null;
  /** A soft-deleted customer is still listed: a debt is not cancelled by a delete. */
  customer_deleted: boolean;
  ticket_count: number;
  unpriced_ticket_count: number;
  outstanding_ticket_count: number;
  oldest_outstanding_days: number | null;
  revenue: number | null;
  paid: number | null;
  billed_unpaid: number | null;
  unbilled: number | null;
  outstanding: number | null;
  aging: ArAging;
}

export interface ArAgingTotals {
  customer_count: number;
  ticket_count: number;
  unpriced_ticket_count: number;
  revenue: number | null;
  paid: number | null;
  billed_unpaid: number | null;
  unbilled: number | null;
  outstanding: number | null;
  aging: ArAging;
}

export interface ArAgingReportData {
  range: { date_from: string; date_to: string };
  aging_key: 'tickets.approved_at';
  buckets: ArAgingBucket[];
  /** Ordered by `outstanding` DESC on the server. The screen keeps that order. */
  rows: ArAgingCustomerRow[];
  totals: ArAgingTotals;
}

/**
 * The column headings for the four buckets.
 *
 * Built from the payload's own `buckets` array rather than hard-coded, so a
 * bucket edge the API moves cannot leave a stale label over a live figure —
 * the silent-wrong-value failure this file's siblings are full of. The order
 * the server sends is the order they render in.
 *
 * `0_30` has no `min_days` and `90_plus` has no `max_days`; each open end is
 * spelled rather than guessed at.
 */
export function bucketLabel(bucket: ArAgingBucket): string {
  if (bucket.min_days === null && bucket.max_days !== null) return `0–${bucket.max_days}`;
  if (bucket.max_days === null && bucket.min_days !== null) return `${bucket.min_days}+`;
  if (bucket.min_days === null && bucket.max_days === null) return 'All';
  return `${bucket.min_days}–${bucket.max_days}`;
}

/** What a row is called when it has no customer. Never "Unknown" — see the docblock. */
export const NO_CUSTOMER_LABEL = 'No customer on ticket';

/**
 * How a row's name reads.
 *
 * Three separate states and three separate spellings:
 *   - `customer_id` absent  → "No customer on ticket" (a fact about the ticket)
 *   - a name                → the name
 *   - an id with no name    → an em dash, because the screen was handed an id
 *                             it cannot render and must not invent a label for
 */
export function customerLabel(row: ArAgingCustomerRow): string {
  if (row.customer_id === null) return NO_CUSTOMER_LABEL;
  return row.customer_name ?? '—';
}
